"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUp, InstagramLogo, LinkedinLogo } from "phosphor-react";
import { COMPANY, SOCIAL_LINKS, FOOTER } from "@/lib/constants";
import { useTranslations } from "@/app/providers";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export function Footer() {
  const t = useTranslations();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const companyFields = [
    { key: "legalName", value: COMPANY.legalName },
    { key: "registeredOffice", value: COMPANY.legalAddress, isAddress: true },
    { key: "vatNumber", value: COMPANY.vatNumber },
    
   
   
    { key: "pec", value: COMPANY.pec, isLink: true },
    { key: "shareCapital", value: COMPANY.shareCapital },
    { key: "rea", value: COMPANY.rea },
  ];

  const legalLinks = [
    {
      href: FOOTER.links.privacy,
      labelKey: "privacyPolicy" as keyof typeof t.footer,
    },
    {
      href: FOOTER.links.cookie,
      labelKey: "cookiePolicy" as keyof typeof t.footer,
    },
  ];

  const socialLinks = [
    {
      url: SOCIAL_LINKS.instagram,
      icon: InstagramLogo,
      ariaLabelKey: "visitInstagram" as keyof typeof t.footer,
    },
    {
      url: SOCIAL_LINKS.linkedin,
      icon: LinkedinLogo,
      ariaLabelKey: "visitLinkedIn" as keyof typeof t.footer,
    },
  ].filter((link) => link.url);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative w-full pt-40 pb-12 md:pb-30">
      {/* Blob visibile sopra la sezione contatti */}
      <div className="absolute inset-x-0 -top-[350px] h-[600px] pointer-events-none z-[1]">
        <Image
          src="/footer-blob.png"
          alt=""
          fill
          className="object-cover object-top"
          priority={false}
          aria-hidden="true"
        />
      </div>
      
      {/* Footer background - copre il blob nel footer */}
      <div className="absolute inset-x-0 top-0 bottom-0 bg-brand-cinder-950 z-[2]" />
      
      <div className="relative z-10 container mx-auto px-6 lg:px-0">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-[88px]">
          {/* Left Section - Logo and Slogan */}
          <div className="flex flex-col gap-4">
            {/* Logo */}
            <div className="flex items-center" aria-label="GeeksCreations Logo">
              <Image
                src="/GEEKS-logo-09 1.svg"
                alt="GeeksCreations Logo"
                width={128}
                height={40}
                className="h-5 w-auto"
                priority
              />
            </div>

            {/* Slogan */}
            <div className="text-white">
              <div className="text-[80px] font-semibold leading-[90%] tracking-normal">
                <span className="block">Born</span>
                <span className="block italic">From</span>
                <span className="block">
                  Code<span className="text-brand-violet-500">.</span>
                </span>
              </div>
            </div>
          </div>

        <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12">
            {/* Middle Section - Company Info */}
            <div className="flex flex-col gap-6 md:gap-8 mt-16 md:mt-0">
              {/* Company Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {companyFields.map((field) => {
                  const labelKey = field.key as keyof typeof t.footer;
                  const label = t.footer[labelKey] || "";
                  return (
                    <div key={field.key}>
                      <p className="text-brand-violet-100 text-sm uppercase mb-1">
                        {label}
                      </p>
                      {field.isLink ? (
                        <a
                          href={`mailto:${field.value}`}
                          className="text-white text-sm hover:underline hover:text-brand-violet-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet-400 rounded"
                          aria-label={`Send email to ${field.value}`}
                        >
                          {field.value}
                        </a>
                      ) : field.isAddress ? (
                        <address className="text-white text-sm not-italic">
                          {field.value}
                        </address>
                      ) : (
                        <p className="text-white text-sm">{field.value}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Section - Copyright, Legal and Social */}
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mt-12 md:mt-auto">
            {/* Copyright and Legal Links */}
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-white text-sm">{FOOTER.copyright}</p>
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet-400 rounded"
                  style={{ color: 'hsla(240, 100%, 79%, 1)' }}
                >
                  {t.footer[link.labelKey]}
                </Link>
              ))}
            </div>

            {/* Social Media */}
            <ul className="flex gap-4 list-none">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <li key={social.url}>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-brand-violet-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet-400 rounded"
                      aria-label={t.footer[social.ariaLabelKey]}
                    >
                      <Icon className="w-8 h-8" aria-hidden="true" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        </div>
      </div>

      {/* Scroll to Top Button - Hidden on mobile */}
      <Button
        onClick={scrollToTop}
        size="icon"
        className={cn(
          "fixed bottom-6 right-6 z-50 transition-all duration-300 hidden md:flex",
          showScrollTop
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none"
        )}
        aria-label={t.footer.backToTop}
      >
        <ArrowUp className="w-6 h-6" aria-hidden="true" />
      </Button>
    </footer>
  );
}
